/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, Input, ViewChild } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { convertForDataTest, EMPTY_STRING, isNotUndefined } from '@common/tech-shared';
import { uniqueId } from 'lodash-es';
import { ButtonComponent } from '../../button/button.component';
import { TooltipDirective } from '../../tooltip/tooltip.directive';
import { textInputVariants, TextInputVariants } from '../text-input/text-input.component';

export const LABEL_SHOW_PASSWORD: string = 'Passwort anzeigen';
export const LABEL_HIDE_PASSWORD: string = 'Passwort ausblenden';

@Component({
  selector: 'ods-password-field',
  imports: [CommonModule, ReactiveFormsModule, ButtonComponent, TooltipDirective],
  template: `
    <label [for]="id" class="text-md text-text mb-2 block font-medium" [ngClass]="!showLabel ? 'sr-only' : null">
      {{ inputLabel }}
      @if (required) {
        <i aria-hidden="true">*</i>
      }
    </label>
    <div class="relative">
      <input
        [type]="showPassword ? 'text' : 'password'"
        [id]="id"
        [formControl]="fieldControl"
        [ngClass]="[textInputVariants({ variant }), 'pr-10']"
        [placeholder]="placeholder"
        [autocomplete]="autocomplete"
        [attr.aria-required]="required"
        [attr.aria-invalid]="isError"
        [attr.data-test-id]="_dataTestId + '-password-input'"
        [attr.aria-describedby]="ariaDescribedBy"
        #inputElement
      />
      <div class="absolute top-1/2 right-0.5 -translate-y-1/2">
        <ods-button
          variant="ghost"
          [destructive]="isError"
          size="fit"
          [ariaPressed]="showPassword"
          [dataTestId]="_dataTestId + '-visibility-toggle'"
          [tooltip]="showPassword ? LABEL_HIDE_PASSWORD : LABEL_SHOW_PASSWORD"
          [tooltipAriaType]="'aria-label'"
          (clicked)="toggleVisibility()"
          [icon]="showPassword ? 'visibility-off' : 'visibility'"
          [iconConfig]="{ fill: isError ? 'error' : 'primary', size: 'small' }"
        />
      </div>
    </div>
    <ng-content select="[error]" />
  `,
  host: { class: 'block' },
})
export class PasswordFieldComponent implements AfterViewInit {
  @Input({ required: true }) set label(label: string) {
    this.inputLabel = label;
    this.id = uniqueId(convertForDataTest(label) + '-');
    if (!this._dataTestId) this._dataTestId = convertForDataTest(this.inputLabel);
  }
  @Input() placeholder: string = '';
  @Input() autocomplete: string = 'current-password';
  @Input() variant: TextInputVariants['variant'];
  @Input() fieldControl: FormControl = new FormControl(EMPTY_STRING);
  @Input() required: boolean = false;
  @Input() showLabel: boolean = true;
  @Input() focus: boolean = false;
  @Input() set dataTestId(value: string) {
    if (isNotUndefined(value)) this._dataTestId = value;
  }
  @Input() ariaDescribedBy: string = null;

  @ViewChild('inputElement') inputElement: ElementRef<HTMLInputElement>;

  public inputLabel: string;
  public id: string;
  public _dataTestId: string;
  public showPassword: boolean = false;

  public readonly textInputVariants = textInputVariants;
  public readonly LABEL_SHOW_PASSWORD = LABEL_SHOW_PASSWORD;
  public readonly LABEL_HIDE_PASSWORD = LABEL_HIDE_PASSWORD;

  ngAfterViewInit(): void {
    if (this.focus) this._focusInput();
  }

  _focusInput(): void {
    setTimeout(() => this.inputElement.nativeElement.focus(), 0);
  }

  public get isError(): boolean {
    return this.variant === 'error';
  }

  public toggleVisibility(): void {
    this.showPassword = !this.showPassword;
  }
}
